import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getActivities, saveActivity } from "../../components/Api/GmsActivity";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  activity_Id: 0,
  activity_NameEn: "",
  activity_NameAr: "",
  activity_Description: "",
  activity_Status: true,
  activity_CreatedDate: new Date().toISOString(),
  activity_CreatedBy: 1,
  activity_ModifiedDate: new Date().toISOString(),
  activity_ModifiedBy: 1,
  activity_Number: "",
};

const emptyFilter = {
  activity_Id: null,
  activity_Status: null,
  activity_NameEn: "",
};

export default function ActivityManagementPage() {
  const [activities, setActivities] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsActivityManagement');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsActivityManagement');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsActivityManagement');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsActivityManagement');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => (page - 1) * pageSize + row.index + 1,
      },
      {
        header: "Activity Name (En)",
        accessorKey: "activity_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Activity Name (Ar)",
        accessorKey: "activity_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Description",
        accessorKey: "activity_Description",
        enableColumnFilter: false,
        cell: ({ value }) => value || "N/A",
      },
      {
        header: "Active",
        accessorKey: "activity_Status",
        enableColumnFilter: false,
        cell: ({ value }) => (value ? "Yes" : "No"),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.original);
            }}
            className="btn btn-link p-0"
            disabled={editLoadingRow === row.original.activity_Id}
          >
            {editLoadingRow === row.original.activity_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <SquarePen size={20} color="black" />
            )}
          </button>
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, page, pageSize]
  );

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadActivities(); // Load activities on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadActivities();
    }
  }, [hasAppliedFilter]);

  const loadActivities = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getActivities(filterData);
      setActivities(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load activities: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };



  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.activity_NameEn) {
      Swal.fire("Error", "Please fill in all required fields", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const activityData = {
        ...form,
        activity_CreatedBy: isEditing ? form.activity_CreatedBy : session.userId,
        activity_ModifiedBy: session.userId,
        activity_ModifiedDate: new Date().toISOString(),
      };

      const result = await saveActivity(activityData);
      
      if (result.activity_Id) {
        Swal.fire("Success", "Activity saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadActivities();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save activity";
      
      // Extract error message from API response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          // Extract just the error message from the exception string
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (activity) => {
    setEditLoadingRow(activity.activity_Id);
    try {
      setForm({
        activity_Id: activity.activity_Id,
        activity_NameEn: activity.activity_NameEn || "",
        activity_NameAr: activity.activity_NameAr || "",
        activity_Description: activity.activity_Description || "",
        activity_Status: activity.activity_Status,
        activity_CreatedDate: activity.activity_CreatedDate,
        activity_CreatedBy: activity.activity_CreatedBy,
        activity_ModifiedDate: activity.activity_ModifiedDate,
        activity_ModifiedBy: activity.activity_ModifiedBy,
        activity_Number: activity.activity_Number || "",
      });
      setIsEditing(true);
      setActiveTab("1"); // Switch to form tab
    } catch (error) {
      Swal.fire("Error", "Failed to load activity data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadActivities(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setActivities([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedActivities = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return activities.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh activities when switching to Activity List tab
      if (tab === "2") {
        loadActivities();
      }
    }
  };



  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Activity Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Activity Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Activity List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  {!isViewOnly && (
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={handleSubmit}>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="activity_NameEn">Activity Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="activity_NameEn"
                                value={form.activity_NameEn}
                                onChange={(e) => handleFormChange("activity_NameEn", e.target.value)}
                                placeholder="Enter activity name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="activity_NameAr">Activity Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="activity_NameAr"
                                value={form.activity_NameAr}
                                onChange={(e) => handleFormChange("activity_NameAr", e.target.value)}
                                placeholder="Enter activity name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={8}>
                            <FormGroup>
                              <Label for="activity_Description">Description (Optional)</Label>
                              <Input
                                type="textarea"
                                id="activity_Description"
                                rows="3"
                                value={form.activity_Description}
                                onChange={(e) => handleFormChange("activity_Description", e.target.value)}
                                placeholder="Enter activity description"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <div className="form-check form-switch">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="activity_Status"
                                  name="activity_Status"
                                  checked={form.activity_Status}
                                  onChange={(e) => handleFormChange("activity_Status", e.target.checked)}
                                  disabled={!hasDeletePermission}
                                />
                                <label className="form-check-label" htmlFor="activity_Status">
                                  {form.activity_Status ? "Active" : "Inactive"}
                                </label>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Activity" : "Save Activity"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        </Form>
                      </div>
                    </TabPane>
                  )}
                  <TabPane tabId="2">
                    <div>
                      {/* Filter Section */}
                      <Card>
                        <CardHeader>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">Filter Activities</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                        </CardHeader>
                        {showFilters && (
                        <CardBody>
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_activity_NameEn">Activity Name</Label>
                                <Input
                                  type="text"
                                  id="filter_activity_NameEn"
                                  value={filter.activity_NameEn}
                                  onChange={(e) => handleFilterChange("activity_NameEn", e.target.value)}
                                  placeholder="Enter activity name"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <div className="d-flex justify-content-end">
                                <Button color="secondary" type="button" onClick={handleClearFilter} className="me-2">
                                  Clear Filter
                                </Button>
                                <Button color="primary" type="button" onClick={handleApplyFilter}>
                                  Apply Filter
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </CardBody>
                        )}
                      </Card>

                      {/* Activities Table */}
                      <Card>
                          <CardBody>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="card-title mb-0">
                                Activity List ({totalCount} records)
                              </h5>
                              <div className="d-flex align-items-center">
                                <Label className="me-2">Show:</Label>
                                <Input
                                  type="select"
                                  value={pageSize}
                                  onChange={(e) => {
                                    setPageSize(parseInt(e.target.value));
                                    setPage(1);
                                  }}
                                  style={{ width: "auto" }}
                                >
                                  <option value="10">10</option>
                                  <option value="25">25</option>
                                  <option value="50">50</option>
                                  <option value="100">100</option>
                                </Input>
                              </div>
                            </div>
                            
                            {loading ? (
                              <div className="text-center">
                                <ClipLoader size={50} color="black" />
                              </div>
                            ) : (
                              <>
                                <div className="table-responsive">
                                  <Table bordered>
                                    <thead className="table-light">
                                      <tr>
                                        <th>Sr.No</th>
                                        <th>Activity Name (En)</th>
                                        <th>Activity Name (Ar)</th>
                                        <th>Description</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activities.length === 0 ? (
                                        <tr>
                                          <td colSpan="6" className="text-center">
                                            No activities found
                                          </td>
                                        </tr>
                                      ) : (
                                        getPaginatedActivities().map((activity, index) => (
                                          <tr key={activity.activity_Id}>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>{activity.activity_NameEn}</td>
                                            <td>{activity.activity_NameAr}</td>
                                            <td>{activity.activity_Description || "N/A"}</td>
                                            <td>
                                              <span className={`badge ${activity.activity_Status ? 'bg-success' : 'bg-danger'}`}>
                                                {activity.activity_Status ? "Active" : "Inactive"}
                                              </span>
                                            </td>
                                            <td>
                                              {!isViewOnly && (
                                                <div className="d-flex gap-2">
                                                  <Button
                                                    color="info"
                                                    size="sm"
                                                    onClick={() => handleEdit(activity)}
                                                    disabled={editLoadingRow === activity.activity_Id}
                                                  >
                                                    {editLoadingRow === activity.activity_Id ? (
                                                      <ClipLoader size={16} color="white" />
                                                    ) : (
                                                      "Edit"
                                                    )}
                                                  </Button>
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </Table>
                                </div>

                                {/* Pagination */}
                                <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                     style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                  <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                    <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                    Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                  </div>
                                  {totalCount > pageSize && (
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                      <Button
                                        color="light"
                                        size="sm"
                                        disabled={page === 1}
                                        onClick={() => handlePageChange(page - 1)}
                                        style={{
                                          border: '1px solid #dee2e6',
                                          borderRadius: '0.375rem',
                                          minWidth: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                          color: page === 1 ? '#6c757d' : '#495057'
                                        }}
                                      >
                                        <i className="mdi mdi-chevron-left"></i>
                                      </Button>
                                      
                                      {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                        const pageNum = index + 1;
                                        const isActive = page === pageNum;
                                        
                                        return (
                                          <Button
                                            key={pageNum}
                                            color={isActive ? "primary" : "light"}
                                            size="sm"
                                            onClick={() => handlePageChange(pageNum)}
                                            style={{
                                              border: '1px solid #dee2e6',
                                              borderRadius: '0.375rem',
                                              minWidth: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: isActive ? '#556ee6' : '#fff',
                                              borderColor: isActive ? '#556ee6' : '#dee2e6',
                                              color: isActive ? '#fff' : '#495057',
                                              fontWeight: isActive ? '600' : '400',
                                              boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                            }}
                                          >
                                            {pageNum}
                                          </Button>
                                        );
                                      })}
                                      
                                      <Button
                                        color="light"
                                        size="sm"
                                        disabled={page === Math.ceil(totalCount / pageSize)}
                                        onClick={() => handlePageChange(page + 1)}
                                        style={{
                                          border: '1px solid #dee2e6',
                                          borderRadius: '0.375rem',
                                          minWidth: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                          color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                        }}
                                      >
                                        <i className="mdi mdi-chevron-right"></i>
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </CardBody>
                        </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 